import { useState } from "react";
import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Chip,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
    DatePicker,
    Avatar,
} from "@heroui/react";
import {
    LuPencil,
    LuTrash,
    LuPlus,
    LuEye,
    LuRefreshCw,
    LuCircleUserRound,
} from "react-icons/lu";
import { useDataTable, useFetchData } from "@/hooks";
import {
    ActiveFilterChips,
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
    DeleteConfirmModal,
    FilterButton,
    ChangeStatusModal,
} from "@/components/data-table";
import {
    ThemedText,
    PageHeader,
    NoImageFallback,
    ImageLightbox,
    ErrorState,
    EmptyState,
} from "@/components/ui";
import { FilterData } from "./components";
import { formatCurrency } from "@/utils";
import { useUser } from "@/context/UserContext";
import { parseDate } from "@internationalized/date";

// Initial filter values
const initialFilters = {
    name: "",
    position_id: "",
    department_id: "",
    employment_status_id: "",
};

const StaffData = () => {
    const { hasPermission } = useUser();
    const [lightbox, setLightbox] = useState({ isOpen: false, images: [] });

    const table = useDataTable(
        "staff/data",
        "staff/{id}/delete",
        {},
        initialFilters,
    );

    // Modal state for employment status change
    const [statusModal, setStatusModal] = useState({
        isOpen: false,
        item: null,
    });

    // Fetch filter options
    const { data: positions } = useFetchData("select/position");
    const { data: departments } = useFetchData("select/department");
    const { data: employmentStatuses } = useFetchData(
        "select/employment-status",
    );

    // Filter options to d to FilterData component
    const filterOptions = {
        positions: positions || [],
        departments: departments || [],
        employmentStatuses: employmentStatuses || [],
    };

    // Filter labels
    const filterLabels = {
        name: { label: "Nama Staff" },
        position_id: {
            label: "Jabatan",
            getValueLabel: (val) => {
                const item = positions?.find(
                    (s) => String(s.value) === String(val),
                );
                return item?.title || val;
            },
        },
        department_id: {
            label: "Departemen",
            getValueLabel: (val) => {
                const item = departments?.find(
                    (s) => String(s.value) === String(val),
                );
                return item?.title || val;
            },
        },
        employment_status_id: {
            label: "Status",
            getValueLabel: (val) => {
                const item = employmentStatuses?.find(
                    (s) => String(s.value) === String(val),
                );
                return item?.title || val;
            },
        },
    };

    // Handle remove single filter
    const handleRemoveFilter = (key) => {
        table.filterPanel.setPending((prev) => ({
            ...prev,
            [key]: initialFilters[key],
        }));
        table.filters.setFilter(key, initialFilters[key]);
    };

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data Staff"
                    description="Terjadi kesalahan saat memuat data staff. Silakan coba lagi."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Staff", skeletonWidth: "w-40" },
        { name: "Jabatan", skeletonWidth: "w-32" },
        { name: "Departemen", skeletonWidth: "w-32" },
        { name: "Jenis Gaji", skeletonWidth: "w-24" },
        { name: "Gaji", skeletonWidth: "w-32", align: "end" },
        { name: "Status Kepegawaian", skeletonWidth: "w-24" },
        { name: "Aksi", skeletonWidth: "w-20", align: "end" },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Staff"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Master Data" },
                        { label: "Staff" },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <FilterButton
                        isOpen={table.filterPanel.isOpen}
                        onToggle={() =>
                            table.filterPanel.setIsOpen(
                                !table.filterPanel.isOpen,
                            )
                        }
                        activeCount={table.filters.activeCount}
                    />
                    {hasPermission("staff.create") && (
                        <Button
                            as={Link}
                            to="/staff/create"
                            color="primary"
                            startContent={<LuPlus />}
                            size="sm"
                        >
                            Tambah Staff
                        </Button>
                    )}
                </div>
            </div>

            {/* Filter Panel */}
            <FilterData
                table={table}
                initialFilters={initialFilters}
                filterOptions={filterOptions}
            />

            {/* Active Filter Chips */}
            <ActiveFilterChips
                filters={table.filters.values}
                filterLabels={filterLabels}
                onRemove={handleRemoveFilter}
            />

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title={
                                table.filters.activeCount > 0
                                    ? "Data Tidak Ditemukan"
                                    : "Data Kosong"
                            }
                            description={
                                table.filters.activeCount > 0
                                    ? "Tidak ada staff yang cocok dengan filter."
                                    : "Belum ada data staff."
                            }
                        />
                    ) : (
                        <Table aria-label="Tabel Staff" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="staff">Staff</TableColumn>
                                <TableColumn key="position">
                                    Jabatan
                                </TableColumn>
                                <TableColumn key="department">
                                    Departemen
                                </TableColumn>
                                <TableColumn key="salary_type">
                                    Jenis Gaji
                                </TableColumn>
                                <TableColumn key="salary" align="end">
                                    Gaji
                                </TableColumn>
                                <TableColumn key="status">
                                    Status Kepegawaian
                                </TableColumn>
                                <TableColumn align="end" key="actions">
                                    Aksi
                                </TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {(table.pagination.page - 1) *
                                                    table.pagination.perPage +
                                                    table.items.indexOf(item) +
                                                    1}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-3">
                                                <Avatar
                                                    src={
                                                        item.profile_image
                                                            ?.url_600
                                                    }
                                                    size="sm"
                                                    radius="full"
                                                    className={
                                                        item.profile_image
                                                            ? "shrink-0 cursor-pointer"
                                                            : "shrink-0"
                                                    }
                                                    onClick={() =>
                                                        item.profile_image &&
                                                        setLightbox({
                                                            isOpen: true,
                                                            images: [
                                                                item.profile_image,
                                                            ],
                                                        })
                                                    }
                                                    showFallback
                                                    fallback={
                                                        <NoImageFallback
                                                            icon={
                                                                LuCircleUserRound
                                                            }
                                                            iconSize={18}
                                                        />
                                                    }
                                                />
                                                <div className="flex flex-col">
                                                    <ThemedText size="sm">
                                                        {item.name || "-"}
                                                    </ThemedText>
                                                    {item.user && (
                                                        <ThemedText
                                                            size="xs"
                                                            variant="secondary"
                                                        >
                                                            {item.user.email}
                                                        </ThemedText>
                                                    )}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.position?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.department?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.salary_type?.name || "-"}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {formatCurrency(item.salary)}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                color={
                                                    item.employment_status
                                                        ?.badge_color ||
                                                    "default"
                                                }
                                                size="sm"
                                                variant="flat"
                                                className="font-medium"
                                            >
                                                {item.employment_status?.name ||
                                                    "Unknown"}
                                            </Chip>
                                        </TableCell>
                                        <TableCell>
                                            <div className="relative flex items-center justify-end gap-2">
                                                <Tooltip content="Lihat Detail">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                        as={Link}
                                                        to={`/staff/${item.id}/detail`}
                                                    >
                                                        <LuEye
                                                            size={16}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                                {hasPermission("staff.edit") && (
                                                    <Tooltip content="Edit Staff">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            as={Link}
                                                            to={`/staff/${item.id}/edit`}
                                                        >
                                                            <LuPencil
                                                                size={16}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {hasPermission("staff.edit") && (
                                                    <Tooltip content="Ubah Status Kepegawaian">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            onPress={() =>
                                                                setStatusModal({
                                                                    isOpen: true,
                                                                    item: item,
                                                                })
                                                            }
                                                        >
                                                            <LuRefreshCw
                                                                size={16}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {hasPermission("staff.delete") && (
                                                    <Tooltip content="Hapus Staff">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            color="danger"
                                                            variant="light"
                                                            onPress={() =>
                                                                table.deleteState.openDeleteModal(
                                                                    item.id,
                                                                )
                                                            }
                                                        >
                                                            <LuTrash size={16} />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>

            <DeleteConfirmModal table={table} />

            {/* Employment Status Change Modal */}
            <ChangeStatusModal
                isOpen={statusModal.isOpen}
                onClose={() => setStatusModal({ isOpen: false, item: null })}
                item={statusModal.item}
                itemName={statusModal.item?.name}
                title="Ubah Status Kepegawaian"
                statusEndpoint="select/employment-status"
                statusField="employment_status_id"
                endpoint="staff/{id}/change-employment-status"
                statusIdsRequiringCustomContent={[4, 5]}
                renderCustomContent={(customData, setCustomData) => (
                    <DatePicker
                        label="Tanggal Berhenti"
                        value={
                            customData.end_date
                                ? parseDate(customData.end_date)
                                : null
                        }
                        variant="bordered"
                        onChange={(date) =>
                            setCustomData({
                                ...customData,
                                end_date: date?.toString(),
                            })
                        }
                        isRequired
                        classNames={{
                            label: "text-default-600",
                        }}
                        showMonthAndYearPickers
                        popoverProps={{
                            portalContainer: document.body,
                            classNames: {
                                content: "z-[9999]",
                            },
                        }}
                    />
                )}
                validateCustomContent={(customData) => {
                    if (!customData.end_date) {
                        return "Tanggal berhenti wajib diisi";
                    }
                    return null;
                }}
                statusIdsRequiringConfirmation={
                    statusModal.item?.end_date ? [1, 2, 3] : []
                }
                confirmationMessage="Jika status diubah ke Aktif/Non-Aktif/Cuti, tanggal berhenti akan dihapus."
                onSuccess={() => table.refetch()}
            />

            <ImageLightbox
                isOpen={lightbox.isOpen}
                onClose={() => setLightbox({ ...lightbox, isOpen: false })}
                images={lightbox.images}
            />
        </div>
    );
};

export default StaffData;
